import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Input, message } from 'antd';
import apiService from '../../services/api';
import { ServiceType } from '../../services/api';

const SignUpContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: linear-gradient(
    135deg,
    #181A20 0%,
    #1a1d26 25%,
    #0f1115 50%,
    #1a1d26 75%,
    #181A20 100%
  );
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
      circle at 20% 20%,
      rgba(14, 203, 129, 0.05) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 80% 80%,
      rgba(14, 203, 129, 0.03) 0%,
      transparent 50%
    );
    pointer-events: none;
  }
`;

const SignUpCard = styled.div`
  width: 100%;
  max-width: 420px;
  padding: 40px;
  background-color: #1E2329;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  border: 1px solid #2B3139;
  position: relative;
  z-index: 1;
`;

const Logo = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 20px;

  img {
    height: 60px;
    width: auto;
  }
`;

const Title = styled.h1`
  text-align: center;
  font-size: 24px;
  margin-bottom: 8px;
  color: #EAECEF;
`;

const Subtitle = styled.p`
  text-align: center;
  font-size: 14px;
  color: #848E9C;
  margin-bottom: 24px;
  line-height: 1.5;
`;

const SignInText = styled.div`
  text-align: center;
  margin-top: 20px;
  font-size: 14px;
  color: #848E9C;

  a {
    color: ${({ theme }) => theme.colors.text.primary};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const SignUpButton = styled(Button)`
  width: 100%;
  height: 40px;
  background-color: #0ECB81 !important;
  border: none !important;
  font-weight: 600;
  color: #0B0E11 !important;
  transition: opacity 0.3s ease;

  &:hover {
    background-color: #0ECB81 !important;
    opacity: 0.8 !important;
  }

  &:focus {
    background-color: #0ECB81 !important;
    opacity: 1 !important;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  color: #EAECEF;
  font-size: 14px;
  font-weight: 500;
`;

const ErrorText = styled.div`
  color: #F6465D;
  font-size: 12px;
  margin-top: 4px;
  min-height: 16px;
`;

const FooterText = styled.div`
  margin-top: 20px;
  font-size: 14px;
  color: #848E9C;
  text-align: center;
  position: relative;
  z-index: 1;

  a {
    color: ${({ theme }) => theme.colors.text.primary};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const StyledInput = styled(Input)`
  background-color: #2B3139 !important;
  border: 1px solid #474D57 !important;
  color: #EAECEF !important;

  &::placeholder {
    color: #848E9C !important;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary} !important;
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary} !important;
    box-shadow: 0 0 0 2px rgba(14, 203, 129, 0.1) !important;
  }

  input {
    background-color: transparent !important;
    color: #EAECEF !important;
  }
`;

const StyledPasswordInput = styled(Input.Password)`
  background-color: #2B3139 !important;
  border: 1px solid #474D57 !important;

  input {
    background-color: transparent !important;
    color: #EAECEF !important;
  }

  &::placeholder {
    color: #848E9C !important;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary} !important;
  }

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.primary} !important;
    box-shadow: 0 0 0 2px rgba(14, 203, 129, 0.1) !important;
  }

  .ant-input {
    background-color: transparent !important;
    color: #EAECEF !important;
  }

  .ant-input-suffix {
    color: #848E9C !important;
  }
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 24px;
`;

const StepDot = styled.div<{ active: boolean; completed: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ active, completed }) =>
    completed ? '#0ECB81' : active ? '#0ECB81' : '#474D57'};
  opacity: ${({ active, completed }) => (active || completed ? 1 : 0.5)};
  transition: all 0.3s ease;
`;

const ResendText = styled.div`
  text-align: center;
  margin-top: 12px;
  font-size: 13px;
  color: #848E9C;

  button {
    background: none;
    border: none;
    color: ${({ theme }) => theme.colors.text.primary};
    cursor: pointer;
    padding: 0;
    font-size: 13px;

    &:hover {
      text-decoration: underline;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }
`;

const coreService = apiService.getService(ServiceType.CORE);

type Step = 'form' | 'code' | 'password';

const SignUpPage: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('form');
  const [loading, setLoading] = useState(false);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [nameError, setNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [codeError, setCodeError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');

  const validateName = (value: string) => {
    if (!value) {
      setNameError('Name is required');
      return false;
    }
    if (value.trim().length < 2) {
      setNameError('Name must be at least 2 characters long');
      return false;
    }
    setNameError('');
    return true;
  };

  const validateEmail = (value: string) => {
    if (!value) {
      setEmailError('Email is required');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    setEmailError('');
    return true;
  };

  const validateCode = (value: string) => {
    if (!value) {
      setCodeError('Verification code is required');
      return false;
    }
    if (value.trim().length < 4) {
      setCodeError('Please enter the code from your email');
      return false;
    }
    setCodeError('');
    return true;
  };

  const validatePassword = (value: string) => {
    if (!value) {
      setPasswordError('Password is required');
      return false;
    }
    if (value.length < 6) {
      setPasswordError('Password must be at least 6 characters long');
      return false;
    }
    setPasswordError('');
    return true;
  };

  const validateConfirmPassword = (value: string) => {
    if (!value) {
      setConfirmPasswordError('Please confirm your password');
      return false;
    }
    if (value !== password) {
      setConfirmPasswordError('Passwords do not match');
      return false;
    }
    setConfirmPasswordError('');
    return true;
  };

  const handleSignUp = async () => {
    const isNameValid = validateName(name);
    const isEmailValid = validateEmail(email);
    if (!isNameValid || !isEmailValid) return;

    setLoading(true);
    try {
      await coreService.post('/auth/signup', {
        name: name.trim(),
        email,
      });
      message.success('Verification code sent. Please check your email.');
      setStep('code');
    } catch (error: any) {
      console.error('Sign up error:', error);
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else if (error.response?.status === 409) {
        message.error('Email already exists. Please sign in or use a different email.');
      } else {
        message.error('Failed to sign up. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!validateCode(code)) return;
    setLoading(true);
    try {
      await coreService.post('/auth/verify-code', { email, code: code.trim() });
      message.success('Code verified.');
      setStep('password');
    } catch (error: any) {
      console.error('Verify code error:', error);
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Invalid or expired code. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSetPassword = async () => {
    const isPwdValid = validatePassword(password);
    const isConfirmValid = validateConfirmPassword(confirmPassword);
    if (!isPwdValid || !isConfirmValid) return;

    setLoading(true);
    try {
      await coreService.post('/auth/reset-password', {
        email,
        code: code.trim(),
        newPassword: password,
      });
      message.success('Account created. Please sign in.');
      navigate('/signin');
    } catch (error: any) {
      console.error('Set password error:', error);
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Failed to set password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    try {
      await coreService.post('/auth/forgot-password', { email });
      message.success('Verification code resent.');
    } catch (error: any) {
      console.error('Resend code error:', error);
      message.error('Failed to resend code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    if (step === 'form') {
      return (
        <>
          <Subtitle>Create your account. We'll email you a 6-digit code to verify.</Subtitle>
          <div style={{ marginBottom: '8px' }}>
            <Label>Full Name</Label>
            <StyledInput
              placeholder="Enter your full name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (nameError) validateName(e.target.value);
              }}
              onBlur={(e) => validateName(e.target.value)}
              onPressEnter={handleSignUp}
              size="large"
              status={nameError ? 'error' : ''}
              autoFocus
            />
            <ErrorText>{nameError}</ErrorText>
          </div>

          <div style={{ marginBottom: '8px' }}>
            <Label>Email</Label>
            <StyledInput
              placeholder="Email address"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) validateEmail(e.target.value);
              }}
              onBlur={(e) => validateEmail(e.target.value)}
              onPressEnter={handleSignUp}
              size="large"
              status={emailError ? 'error' : ''}
            />
            <ErrorText>{emailError}</ErrorText>
          </div>

          <SignUpButton onClick={handleSignUp} loading={loading}>
            SIGN UP
          </SignUpButton>
        </>
      );
    }

    if (step === 'code') {
      return (
        <>
          <Subtitle>
            We sent a 6-digit code to <strong style={{ color: '#EAECEF' }}>{email}</strong>. Enter it below.
          </Subtitle>
          <div style={{ marginBottom: '8px' }}>
            <Label>Verification Code</Label>
            <StyledInput
              placeholder="Enter code"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                if (codeError) validateCode(e.target.value);
              }}
              onBlur={(e) => validateCode(e.target.value)}
              onPressEnter={handleVerifyCode}
              size="large"
              status={codeError ? 'error' : ''}
              autoFocus
            />
            <ErrorText>{codeError}</ErrorText>
          </div>
          <SignUpButton onClick={handleVerifyCode} loading={loading}>
            VERIFY CODE
          </SignUpButton>
          <ResendText>
            Didn't receive a code?{' '}
            <button onClick={handleResend} disabled={loading} type="button">
              Resend
            </button>
          </ResendText>
        </>
      );
    }

    return (
      <>
        <Subtitle>Choose a password to finish creating your account.</Subtitle>
        <div style={{ marginBottom: '8px' }}>
          <Label>Password</Label>
          <StyledPasswordInput
            placeholder="Choose a password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (passwordError) validatePassword(e.target.value);
              if (confirmPassword && confirmPasswordError) validateConfirmPassword(confirmPassword);
            }}
            onBlur={(e) => validatePassword(e.target.value)}
            onPressEnter={handleSetPassword}
            size="large"
            status={passwordError ? 'error' : ''}
            autoFocus
          />
          <ErrorText>{passwordError}</ErrorText>
        </div>

        <div style={{ marginBottom: '8px' }}>
          <Label>Confirm Password</Label>
          <StyledPasswordInput
            placeholder="Confirm password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              if (confirmPasswordError) validateConfirmPassword(e.target.value);
            }}
            onBlur={(e) => validateConfirmPassword(e.target.value)}
            onPressEnter={handleSetPassword}
            size="large"
            status={confirmPasswordError ? 'error' : ''}
          />
          <ErrorText>{confirmPasswordError}</ErrorText>
        </div>

        <SignUpButton onClick={handleSetPassword} loading={loading}>
          CREATE ACCOUNT
        </SignUpButton>
      </>
    );
  };

  return (
    <SignUpContainer>
      <SignUpCard>
        <Logo>
          <img src="/Algotrade - White.png" alt="Algotrade" />
        </Logo>
        <Title>Sign Up</Title>
        <StepIndicator>
          <StepDot active={step === 'form'} completed={step !== 'form'} />
          <StepDot active={step === 'code'} completed={step === 'password'} />
          <StepDot active={step === 'password'} completed={false} />
        </StepIndicator>
        {renderStep()}
        <SignInText>
          Already have an account? <Link to="/signin">Sign In</Link>
        </SignInText>
      </SignUpCard>

      <FooterText>
        By continuing, you agree to Algotrade's <a href="/terms">Terms of Service</a> and <a href="/privacy">Privacy Policy</a>
      </FooterText>
    </SignUpContainer>
  );
};

export default SignUpPage;
